import { useMemo } from 'react';

import { RectRangeLegend } from './RectRangeLegend/RectRangeLegend.js';
import { XYLegend } from './XYLegend/XYLegend.js';
import { safeGet } from '../../../core/utils/safe-get.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import type {
  SeriesToMetadata,
  XYChartSeriesWithDatapoints,
} from '../../types/xy-chart-internals.js';
import { isRangeLegend } from '../../utils/is-range-legend.js';
import { isRectSeries } from '../../utils/type-guards.js';

/**
 * Builds a stable identity key from series that changes only when the consumer
 * provides different series (added/removed/reordered) or when a series color
 * changes in metadata. Uses series IDs and resolved colors so the legend
 * re-renders when color rules update.
 */
function buildLegendIdentityKey(
  series: XYChartSeriesWithDatapoints[],
  seriesToMetadata: SeriesToMetadata,
): string {
  return series
    .map((s) => {
      const metadata = safeGet(seriesToMetadata, s);
      const color = 'color' in metadata ? metadata.color.Default : '';
      return `${s.id}:${color}`;
    })
    .join(',');
}

export const XYChartLegend = () => {
  const { seriesWithDatapoints, seriesToMetadata } = useXYChartData();

  const identityKey = buildLegendIdentityKey(
    seriesWithDatapoints,
    seriesToMetadata,
  );

  // Deps use identityKey (not the object references) intentionally — we want
  // stable references that only change when series are added/removed/reordered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableSeries = useMemo(() => seriesWithDatapoints, [identityKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableMetadata = useMemo(() => seriesToMetadata, [identityKey]);

  const firstSeries = stableSeries[0];

  if (isRectSeries(firstSeries)) {
    const { valueType: firstSeriesValueType } = safeGet(
      stableMetadata,
      firstSeries,
    );

    if (!firstSeriesValueType) {
      return null;
    }

    if (isRangeLegend(stableMetadata, firstSeries)) {
      return <RectRangeLegend series={firstSeries} />;
    }
  }

  return <XYLegend series={stableSeries} seriesToMetadata={stableMetadata} />;
};
